import { useEffect, useState } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Accordion from '@radix-ui/react-accordion'
import styled from 'styled-components'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { Border, Colors, Dimensions, Spacing, Typography } from '../../styles'
import { pointerQuery } from '../../styles/dimensions'
import { icons } from '../../styles/images'
import { Motion } from '../../styles/motion'
import { isOverviewViewNonEmpty, isTaskOverviewView } from '../../utils/overview'
import { DropType, TOverviewView, TTaskV4 } from '../../utils/types'
import { emptyFunction } from '../../utils/utils'
import Flex from '../atoms/Flex'
import { Icon } from '../atoms/Icon'
import StatusLabel from '../atoms/StatusLabel'
import GTButton from '../atoms/buttons/GTButton'
import { BodyMedium, BodySmall } from '../atoms/typography/Typography'
import ScheduleTaskModal from '../molecules/ScheduleTaskModal'
import GTContextMenu from '../radix/GTContextMenu'
import { getScheduleMenuItem } from '../radix/TaskContextMenuWrapper'
import { getOverviewViewIcon } from './AccordionItem'

const TriggerTitle = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: ${Spacing._12};
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: ${Spacing._8};
    white-space: nowrap;
`
const PreviewTitle = styled(BodySmall)`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${Colors.text.muted};

    ${Dimensions.mediaQuery.phone} {
        display: none;
    }
`
const ListTitle = styled(BodyMedium)<{ isComplete: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    ${Typography.bold}
    ${(props) =>
        props.isComplete &&
        `
        text-decoration: line-through;
        color: ${Colors.text.muted};
    `}
`
const TriggerRightContainer = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: ${Spacing._12};
`
const ItemsRemainingText = styled(BodySmall)`
    display: flex;
    align-items: center;
    gap: ${Spacing._4};
    white-space: nowrap;
    color: ${Colors.text.muted};
`
// Keep the secondary edit affordance quieter than the status and progress around it without making
// its hit area smaller. Touch devices still get the app-wide 44px target floor.
const EditRoutineButton = styled(GTButton)`
    margin: -${Spacing._4} 0;

    & svg {
        width: ${Dimensions.iconSize.small};
        height: ${Dimensions.iconSize.small};
    }

    ${pointerQuery.noHover} {
        min-width: ${Dimensions.TASK_HEIGHT_TOUCH};
        min-height: ${Dimensions.TASK_HEIGHT_TOUCH};
        margin: -${Spacing._12} 0;
    }
`
const StyledFlex = styled(Flex)`
    width: 100%;
`
const Header = styled(Accordion.Header)<{ $isOpen: boolean }>`
    width: 100%;
    margin: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background-color: ${Colors.background.white};
    border: 1px solid ${Colors.background.border};
    border-bottom-color: ${({ $isOpen }) => ($isOpen ? Colors.background.hairline : Colors.background.border)};
    border-radius: ${({ $isOpen }) =>
        $isOpen ? `${Border.radius.medium} ${Border.radius.medium} 0 0` : Border.radius.medium};
    padding: 0 ${Spacing._12};
`
const Trigger = styled(Accordion.Trigger)`
    all: unset;
    min-width: 0;
    flex: 1;
    padding: ${Spacing._12} 0;
    cursor: pointer;
    user-select: none;
`
const Chevron = styled(Icon)`
    transition: transform ${Motion.exit};

    ${Trigger}[data-state='open'] & {
        transform: rotate(180deg);
    }
`

interface AccordionHeaderProps {
    list: TOverviewView
    isOpen: boolean
    activeMeetingPreparationTasks: TTaskV4[]
    showCollapsedPreview: boolean
}
const AccordionHeader = ({
    list,
    isOpen,
    activeMeetingPreparationTasks,
    showCollapsedPreview,
}: AccordionHeaderProps) => {
    const isReadOnly = useIsReadOnly()
    const navigate = useNavigate()
    const location = useLocation()
    const [, drag, dragPreview] = useDrag(
        () => ({
            type: DropType.OVERVIEW_VIEW_HEADER,
            item: { view: list },
            canDrag: isTaskOverviewView(list) && !isReadOnly,
        }),
        [list, isReadOnly]
    )

    useEffect(() => {
        dragPreview(getEmptyImage(), { captureDraggingState: true })
    }, [dragPreview])

    const viewItemLength =
        list.type === 'meeting_preparation' ? activeMeetingPreparationTasks.length : list.view_items.length
    const isRoutine = list.type === 'routine'
    const isRoutineComplete = list.status === 'complete'
    const isNonEmpty = isOverviewViewNonEmpty(list, activeMeetingPreparationTasks.length)
    const previewTitle =
        list.type === 'meeting_preparation'
            ? activeMeetingPreparationTasks[0]?.title
            : list.type === 'routine'
            ? list.view_items.find((item) => 'is_completed' in item && !item.is_completed)?.title
            : list.view_items[0]?.title

    // dragging the header onto the calendar is otherwise the only way to block time for a list
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)

    const triggerContent = (
        <StyledFlex justifyContent="space-between" ref={drag}>
            <TriggerTitle>
                <Icon icon={getOverviewViewIcon(list)} color={viewItemLength === 0 ? 'gray' : 'black'} />
                <ListTitle isComplete={isRoutine ? isRoutineComplete : viewItemLength === 0 && list.is_linked}>
                    {list.name}
                </ListTitle>
                {showCollapsedPreview && !isOpen && isNonEmpty && previewTitle && (
                    <PreviewTitle data-testid="overview-card-preview">{previewTitle}</PreviewTitle>
                )}
            </TriggerTitle>
            <TriggerRightContainer>
                {isRoutine && Boolean(list.streak) && (
                    <ItemsRemainingText>
                        <Icon icon={icons.fire} size="small" color="gray" />
                        {list.streak}
                    </ItemsRemainingText>
                )}
                {isRoutine && list.status === 'current' && <StatusLabel status="Current" color="yellow" />}
                {isRoutine && list.status === 'up_next' && <StatusLabel status="Up next" color="gray" />}
                {isRoutine && list.status === 'complete' && (
                    <StatusLabel status="Routine complete" color="green" icon={icons.check} />
                )}
                {isRoutine && list.status === 'empty' && <StatusLabel status="Empty" color="gray" />}
                {isRoutine && list.status === 'current' && (
                    <ItemsRemainingText>{list.remaining_count} remaining</ItemsRemainingText>
                )}
                {!isRoutine && viewItemLength > 0 && (
                    <ItemsRemainingText>{viewItemLength} remaining</ItemsRemainingText>
                )}
                {!isRoutine &&
                    viewItemLength === 0 &&
                    list.is_linked &&
                    list.has_tasks_completed_today !== undefined && (
                        <StatusLabel
                            status={list.has_tasks_completed_today ? 'List complete' : 'Empty'}
                            color={list.has_tasks_completed_today ? 'green' : 'gray'}
                            icon={icons.check}
                        />
                    )}
                <Chevron icon={icons.caret_down} className="AccordionChevron" />
            </TriggerRightContainer>
        </StyledFlex>
    )

    const trigger = <Trigger>{triggerContent}</Trigger>

    return (
        <>
            <Header $isOpen={isOpen}>
                {isRoutine ? (
                    trigger
                ) : (
                    <GTContextMenu
                        items={[getScheduleMenuItem(() => setIsScheduleModalOpen(true))]}
                        trigger={trigger}
                        onOpenChange={emptyFunction}
                    />
                )}
                {isRoutine && isOpen && (
                    // A sibling of the native accordion trigger: never nest one button inside another.
                    <EditRoutineButton
                        styleType="icon"
                        icon={isReadOnly ? icons.eye : icons.pencil}
                        tooltipText={isReadOnly ? 'View routine' : 'Edit routine'}
                        onClick={() => navigate(`/routines/${list.routine_id}`)}
                        disabled={location.pathname === `/routines/${list.routine_id}`}
                    />
                )}
            </Header>
            {!isRoutine && isScheduleModalOpen && (
                <ScheduleTaskModal view={list} onClose={() => setIsScheduleModalOpen(false)} />
            )}
        </>
    )
}

export default AccordionHeader
